import { useCallback, useEffect, useState } from "react";
import { Container, Form, FormGroup, Input, Label } from "reactstrap";
import toast from "react-hot-toast";
import moment from "moment";

import { CustomButton } from "components/CustomButton";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import { getMcapSettings, updateMcapSettings } from "api/api.service";
import typography from "../typography";

/**
 * MCAP settings — the tunable assumptions behind figures this dashboard
 * publishes.
 *
 * These are ASSUMPTIONS, not measurements, and the copy on this screen says so
 * plainly. `resetBenchmarkMinutes` in particular is a BENCHMARK, not a
 * measurement: the K-12 research does not establish one universal figure for
 * what a classroom transition costs, and nothing in our data observes it
 * either. That is exactly why it lives here rather than in the code — a
 * district adopting a state-specific or newer research figure changes this
 * field, not a deploy — and why every surface reporting time saved must print
 * the value it used alongside it.
 *
 * Deliberately a plain form with an explicit Save, not per-field autosave.
 * Changing any of these silently rewrites numbers an administrator may already
 * have quoted to a school board, so the change should be a decision, not a
 * side effect of tabbing through a field.
 */

// Bounds mirror the backend DTO exactly. Duplicated on purpose: the API is the
// enforcement point, but a user should learn a value is out of range while
// typing rather than after a round trip that returns a 400.
const FIELDS = [
  {
    key: "resetBenchmarkMinutes",
    label: "Reset benchmark",
    unit: "minutes",
    min: 1,
    max: 60,
    help: "How long settling a class back to work is taken to cost WITHOUT a reset. “Time saved” is this benchmark minus the return-to-task time each teacher reports. There is no universal K-12 figure, so this is a starting benchmark and not a national average — change it if your state, district or a newer study puts it elsewhere. Every report prints the value it used.",
  },
  {
    key: "multiSettingThreshold",
    label: "Routine threshold",
    unit: "of 5 reset moments",
    min: 1,
    max: 5,
    help: "How many different reset moments (transition, after lunch, before a test, morning meeting, return from recess) a classroom must use before it counts as embedding resets in its routine rather than trying them once.",
  },
  {
    key: "aggregationThreshold",
    label: "Minimum group size",
    unit: "records",
    min: 1,
    max: 100,
    help: "Aggregates computed from fewer records than this are hidden and shown as “insufficient data”. Raising it protects small classrooms from being identifiable; lowering it shows more, from thinner evidence.",
  },
];

export default function Settings() {
  const [values, setValues] = useState(null);
  const [meta, setMeta] = useState({ updatedAt: null, updatedByName: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setLoadError(null);
      // The API helper resolves with the parsed body itself — there is no
      // `.data` envelope around it. Reading res.data here would leave every
      // field blank while the request quietly succeeded.
      const res = await getMcapSettings(signal);
      if (!res) return;
      setValues({
        resetBenchmarkMinutes: res.resetBenchmarkMinutes,
        multiSettingThreshold: res.multiSettingThreshold,
        aggregationThreshold: res.aggregationThreshold,
      });
      setMeta({ updatedAt: res.updatedAt, updatedByName: res.updatedByName });
    } catch (error) {
      setLoadError("Couldn't load settings. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const invalid = FIELDS.filter(({ key, min, max }) => {
    const v = values?.[key];
    return v === "" || v === null || Number.isNaN(Number(v)) || Number(v) < min || Number(v) > max;
  }).map((f) => f.key);

  const handleChange = (key) => (e) => {
    const raw = e.target.value;
    // Keep the raw string in state while typing so a half-typed value isn't
    // coerced to 0 mid-keystroke; it's parsed on save.
    setValues((prev) => ({ ...prev, [key]: raw }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (invalid.length) return;
    try {
      setSaving(true);
      const body = FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: Number(values[key]) }), {});
      const res = await updateMcapSettings(body);
      if (res) {
        setValues({
          resetBenchmarkMinutes: res.resetBenchmarkMinutes,
          multiSettingThreshold: res.multiSettingThreshold,
          aggregationThreshold: res.aggregationThreshold,
        });
        setMeta({ updatedAt: res.updatedAt, updatedByName: res.updatedByName });
      }
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoaderFallbackUI />;

  return (
    <div className="page-content">
      <Container fluid className="mcap-hub customer-management">
        {loadError ? (
          <div className="standard-typo-14-400">{loadError}</div>
        ) : (
          <div className="mcap-settings">
            <p className="standard-typo-14-400 mcap-settings-intro">
              {typography.SETTINGS_INTRO}
            </p>

            <Form onSubmit={handleSave}>
              {FIELDS.map(({ key, label, unit, min, max, help }) => (
                <FormGroup key={key} className="mcap-setting-row">
                  <Label className="edit-notification-form-label" for={`mcap-${key}`}>
                    {label}
                  </Label>
                  <div className="mcap-setting-control">
                    <Input
                      id={`mcap-${key}`}
                      type="number"
                      min={min}
                      max={max}
                      value={values?.[key] ?? ""}
                      onChange={handleChange(key)}
                      invalid={invalid.includes(key)}
                      className="mcap-setting-input"
                    />
                    <span className="mcap-setting-unit standard-typo-14-400">{unit}</span>
                  </div>
                  <div className="standard-typo-12-400-ld mcap-setting-help">{help}</div>
                  {invalid.includes(key) && (
                    <div className="invalid-feedback d-block">
                      Enter a whole number between {min} and {max}.
                    </div>
                  )}
                </FormGroup>
              ))}

              <div className="mcap-settings-actions">
                {/* color="primary" — see Implementation.js's own download
                    button for why the borrowed className this used to carry
                    made the text unreadable. */}
                <CustomButton
                  title={saving ? "Saving…" : "Save settings"}
                  color="primary"
                  type="submit"
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving || invalid.length > 0}
                />
                {meta.updatedAt && (
                  <span className="standard-typo-12-400-ld mcap-settings-audit">
                    Last changed {moment(meta.updatedAt).format("D MMM YYYY, HH:mm")}
                    {meta.updatedByName ? ` by ${meta.updatedByName}` : ""}
                  </span>
                )}
              </div>
            </Form>
          </div>
        )}
      </Container>
    </div>
  );
}
